import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableFooter,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import * as XLSX from "xlsx";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";
import { downloadReportPdf, PdfColumn } from "./reportPdf";
import { GetEightHoursReport } from "../../../services/asai";


type EmployeeType = "payroll" | "contract" | "cnc";

type ReportRow = {
  personId: string;
  personName: string;
  shift: string;
  inTime: string;
  outTime: string;
  status: string;
  hours: string;
  hoursStatus: string;
  overtime: string;
};

type ColumnKey = keyof ReportRow;

const EMPLOYEE_TABS = [
  { value: "payroll" as EmployeeType, label: "Payroll Report" },
  { value: "contract" as EmployeeType, label: "Contract Report" },
  { value: "cnc" as EmployeeType, label: "CNC Apprentice Report" },
];

const PAGE_TITLES: Record<EmployeeType, string> = {
  payroll: "8 Hours Payroll Report",
  contract: "8 Hours Contract Report",
  cnc: "8 Hours Apprentice Report",
};


const BASE_COLUMNS: Array<{ key: ColumnKey; label: string }> = [
  { key: "personId", label: "ID" },
  { key: "personName", label: "Person Name" },
  { key: "shift", label: "Shift" },
  { key: "inTime", label: "In Time" },
  { key: "outTime", label: "Out Time" },
  { key: "status", label: "Status" },
  { key: "hours", label: "Hours" },
];

const EXTENDED_COLUMNS: Array<{ key: ColumnKey; label: string }> = [
  { key: "hoursStatus", label: "Hours Status" },
  { key: "overtime", label: "Overtime" },
];

const parseResponse = (response: any): ReportRow[] => {
  const result = response?.data?.data ?? response?.data ?? {};

  const records = Array.isArray(result?.result)
    ? result.result
    : Array.isArray(result?.Result)
      ? result.Result
      : [];

  return records.map((item: any) => ({
    personId: String(
      item?.personId ?? item?.PersonId ?? item?.personid ?? "",
    ),
    personName: String(
      item?.personName ?? item?.PersonName ?? item?.name ?? "",
    ),
    shift: String(item?.shift ?? item?.Shift ?? ""),
    inTime: String(
      item?.inTime ?? item?.InTime ?? item?.checkin ?? "-",
    ),
    outTime: String(
      item?.outTime ?? item?.OutTime ?? item?.checkout ?? "-",
    ),
    status: String(item?.status ?? item?.Status ?? "-"),
    hours: String(item?.hours ?? item?.Hours ?? "-"),
    hoursStatus: String(
      item?.hoursStatus ?? item?.HoursStatus ?? item?.gender ?? "",
    ),
    overtime: String(
      item?.overtime ?? item?.Overtime ?? "0.000",
    ),
  }));
};

const getToday = () => new Date().toISOString().slice(0, 10);

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

const TablePaginationActions = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
}: {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    page: number,
  ) => void;
}) => {
  const theme = useTheme();
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(event) => onPageChange(event, 0)} disabled={page === 0}>
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={(event) => onPageChange(event, page - 1)} disabled={page === 0}>
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton onClick={(event) => onPageChange(event, page + 1)} disabled={page >= lastPage}>
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton onClick={(event) => onPageChange(event, lastPage)} disabled={page >= lastPage}>
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
};

const EightHourReportPage: React.FC = () => {
  const [employeeType, setEmployeeType] = useState<EmployeeType>("payroll");
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [columnFilters, setColumnFilters] =
    useState<Partial<Record<ColumnKey, string>>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hiddenColumns, setHiddenColumns] = useState<Set<ColumnKey>>(new Set());
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [orderDirection, setOrderDirection] =
    useState<"asc" | "desc">("asc");
  const [valueToOrderBy, setValueToOrderBy] =
    useState<ColumnKey>("personId");

  const showExtendedColumns = employeeType === "cnc";
  const columns = useMemo(
    () => (showExtendedColumns ? [...BASE_COLUMNS, ...EXTENDED_COLUMNS] : BASE_COLUMNS),
    [showExtendedColumns],
  );
  const visibleColumns = columns.filter(
    (column) => !hiddenColumns.has(column.key),
  );

  const loadReport = async () => {
    if (!selectedDate) return;

    setLoading(true);
    setError("");

    try {
      const response = await GetEightHoursReport({
        employeeType,
        date: selectedDate,
      });

      setRows(parseResponse(response));
      setPage(0);
    } catch (requestError: any) {
      console.error("Failed to load 8-hour report:", requestError);
      setRows([]);
      setError(
        requestError?.response?.data?.message ||
        requestError?.message ||
        "Unable to load the report.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadReport();
  }, [employeeType]);

  const hasColumnFilters = Object.values(columnFilters).some(
    (value) =>
      value !== null &&
      value !== undefined &&
      String(value).trim() !== "",
  );

  const columnFilterCount = Object.values(columnFilters).filter(
    (value) =>
      value !== null &&
      value !== undefined &&
      String(value).trim() !== "",
  ).length;

  const filteredRows = useMemo(() => {
    let data = [...rows];

    const global = searchText.trim().toLowerCase();
    if (global) {
      data = data.filter((row) =>
        columns.some((column) =>
          String(row[column.key] ?? "")
            .toLowerCase()
            .includes(global),
        ),
      );
    }

    Object.entries(columnFilters).forEach(([field, value]) => {
      const search = String(value ?? "").trim().toLowerCase();
      if (!search) return;

      data = data.filter((row) =>
        String(row[field as ColumnKey] ?? "")
          .toLowerCase()
          .includes(search),
      );
    });

    data.sort((first, second) => {
      const firstValue = String(first[valueToOrderBy] ?? "");
      const secondValue = String(second[valueToOrderBy] ?? "");
      const comparison = firstValue.localeCompare(
        secondValue,
        undefined,
        { numeric: true, sensitivity: "base" },
      );
      return orderDirection === "asc" ? comparison : -comparison;
    });

    return data;
  }, [
    rows,
    columns,
    searchText,
    columnFilters,
    valueToOrderBy,
    orderDirection,
  ]);

  const visibleRows = useMemo(
    () =>
      filteredRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [filteredRows, page, rowsPerPage],
  );

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredRows.length / rowsPerPage) - 1,
    );
    if (page > maxPage) setPage(maxPage);
  }, [filteredRows.length, page, rowsPerPage]);

  const toggleColumn = (key: ColumnKey) => {
    setHiddenColumns((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else if (visibleColumns.length > 1) {
        next.add(key);
      }
      return next;
    });
  };

  const handleSort = (field: ColumnKey) => {
    const isAsc = valueToOrderBy === field && orderDirection === "asc";
    setValueToOrderBy(field);
    setOrderDirection(isAsc ? "desc" : "asc");
    setPage(0);
  };

  const clearColumnFilter = (field: ColumnKey) => {
    setColumnFilters((previous) => {
      const updated = { ...previous };
      delete updated[field];
      return updated;
    });
    setPage(0);
  };

  const isColumnFiltered = (field: ColumnKey) =>
    Boolean(columnFilters[field]?.trim());

  const resetTableFilters = () => {
    setSearchText("");
    setColumnFilters({});
    setShowAllFilters(false);
    setPage(0);
  };

  const activeEmployeeLabel =
    EMPLOYEE_TABS.find((tab) => tab.value === employeeType)?.label ?? "";

  const breadcrumbItems = [
    { to: "/", title: "ASAI" },
    { title: "8 Hours Shift Reports" },
    { title: "Report" },
    { title: activeEmployeeLabel },
  ];

  const copyRows = async () => {
    const header = visibleColumns
      .map((column) => column.label)
      .join("\t");

    const body = filteredRows
      .map((row) =>
        visibleColumns
          .map((column) => String(row[column.key] ?? ""))
          .join("\t"),
      )
      .join("\n");

    try {
      await navigator.clipboard.writeText(`${header}\n${body}`);
    } catch {
      setError("Unable to copy the report.");
    }
  };

  const exportExcel = () => {
    const data = filteredRows.map((row) => {
      const record: Record<string, string> = {};
      visibleColumns.forEach((column) => {
        record[column.label] = String(row[column.key] ?? "");
      });
      return record;
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    XLSX.writeFile(
      workbook,
      `${PAGE_TITLES[employeeType].replace(/\s+/g, "_")}_${selectedDate}.xlsx`,
    );
  };

  const exportPdf = () => {
    const pdfColumns: PdfColumn<ReportRow>[] = visibleColumns.map((column) => ({
      key: column.key,
      header: column.label,
    }));

    downloadReportPdf({
      title: PAGE_TITLES[employeeType],
      date: selectedDate,
      rows: filteredRows,
      columns: pdfColumns,
      fileName: `${PAGE_TITLES[employeeType].replace(/\s+/g, "_")}_${selectedDate}.pdf`,
    });
  };

  return (
    <PageContainer title={PAGE_TITLES[employeeType]}>
      <Breadcrumb
        title={PAGE_TITLES[employeeType]}
        items={breadcrumbItems}
      />

      <Box sx={{ px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2.5 } }}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
          }}
        >
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider", px: 2 }}>
            <Tabs
              value={employeeType}
              onChange={(_, value) => {
                setEmployeeType(value);
                setPage(0);
                setSearchText("");
                setHiddenColumns(new Set());
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {EMPLOYEE_TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                width: "100%",
                gap: 1.5,
                mb: 2,
                flexWrap: { xs: "wrap", lg: "nowrap" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 1.5,
                  flexShrink: 0,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label="Date"
                  type="date"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ width: { xs: 220, sm: 250 } }}
                />

                <Button
                  variant="contained"
                  onClick={() => void loadReport()}
                  disabled={loading || !selectedDate}
                  sx={{ minHeight: 40, textTransform: "none", fontWeight: 600 }}
                >
                  Search
                </Button>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 0.75,
                  flex: 1,
                  minWidth: 0,
                  flexWrap: { xs: "wrap", lg: "nowrap" },
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => void copyRows()}
                  disabled={!filteredRows.length}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Copy
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportExcel}
                  disabled={!filteredRows.length}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Excel
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={exportPdf}
                  disabled={!filteredRows.length}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  PDF
                </Button>

                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchText}
                  onChange={(event) => {
                    setSearchText(event.target.value);
                    setPage(0);
                  }}
                  sx={{
                    width: { xs: "100%", md: 200 },
                    minWidth: 120,
                    backgroundColor: "white",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box display="flex" alignItems="center" gap={0.5}>
                  <FilterAltIcon
                    sx={{
                      cursor: "pointer",
                      color: showAllFilters ? "primary.main" : "text.primary",
                    }}
                    onClick={() => {
                      setShowAllFilters((current) => !current);
                      setSearchText("");
                      setPage(0);
                    }}
                  />

                  {hasColumnFilters && !showAllFilters && (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        textTransform: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => setShowAllFilters(true)}
                    >
                      Filters ({columnFilterCount})
                      <CloseIcon
                        sx={{ fontSize: 16 }}
                        onClick={(event) => {
                          event.stopPropagation();
                          resetTableFilters();
                        }}
                      />
                    </Button>
                  )}
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={(event) => setColumnMenuAnchorEl(event.currentTarget)}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Columns
                  {hiddenColumns.size > 0 && ` (${hiddenColumns.size})`}
                </Button>

                <Menu
                  anchorEl={columnMenuAnchorEl}
                  open={Boolean(columnMenuAnchorEl)}
                  onClose={() => setColumnMenuAnchorEl(null)}
                >
                  {columns.map((column) => (
                    <MenuItem
                      key={column.key}
                      onClick={() => toggleColumn(column.key)}
                    >
                      <Checkbox
                        size="small"
                        checked={!hiddenColumns.has(column.key)}
                      />
                      <ListItemText primary={column.label} />
                    </MenuItem>
                  ))}
                </Menu>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  disabled={!searchText.trim() && !hasColumnFilters}
                  onClick={resetTableFilters}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Reset Filter
                </Button>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 600 }}>
              {filteredRows.length} employees
            </Typography>

            {loading ? (
              <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: showAllFilters ? 540 : 440,
                    overflowY: filteredRows.length > 5 ? "auto" : "visible",
                    overflowX: "auto",
                    scrollbarGutter: "stable",
                  }}
                >
                  <Table
                    aria-label="custom pagination table"
                    stickyHeader
                    sx={{ whiteSpace: "nowrap", overflowX: { md: "scroll" } }}
                  >
                    <TableHead>
                      <TableRow>
                        {columns
                          .filter((column) => !hiddenColumns.has(column.key))
                          .map((column) => (
                            <TableCell key={column.key}>
                              <TableSortLabel
                                active={valueToOrderBy === column.key}
                                direction={
                                  valueToOrderBy === column.key
                                    ? orderDirection
                                    : "desc"
                                }
                                onClick={() => handleSort(column.key)}
                              >
                                {column.label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                      </TableRow>

                      {showAllFilters && (
                        <TableRow>
                          {columns
                            .filter((column) => !hiddenColumns.has(column.key))
                            .map((column) => (
                              <TableCell key={column.key}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder="Search..."
                                  value={columnFilters[column.key] || ""}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    setColumnFilters((previous) => {
                                      const updated = { ...previous };
                                      if (value.trim()) {
                                        updated[column.key] = value;
                                      } else {
                                        delete updated[column.key];
                                      }
                                      return updated;
                                    });
                                    setPage(0);
                                  }}
                                  InputProps={{
                                    endAdornment: isColumnFiltered(column.key) ? (
                                      <InputAdornment position="end">
                                        <IconButton
                                          size="small"
                                          onClick={() => clearColumnFilter(column.key)}
                                        >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      </InputAdornment>
                                    ) : undefined,
                                  }}
                                />
                              </TableCell>
                            ))}
                        </TableRow>
                      )}
                    </TableHead>

                    <TableBody>
                      {loading && (
                        <TableRow>
                          <TableCell
                            colSpan={Math.max(visibleColumns.length, 1)}
                            align="center"
                            sx={{ py: 5 }}
                          >
                            <CircularProgress size={28} />
                          </TableCell>
                        </TableRow>
                      )}

                      {!loading && visibleRows.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={Math.max(visibleColumns.length, 1)}
                            align="center"
                          >
                            No data found
                          </TableCell>
                        </TableRow>
                      )}

                      {!loading &&
                        visibleRows.map((row, index) => (
                          <TableRow key={`${row.personId}-${row.inTime}-${row.outTime}-${index}`}>
                            {columns
                              .filter((column) => !hiddenColumns.has(column.key))
                              .map((column) => (
                                <TableCell key={column.key}>
                                  {row[column.key] || "-"}
                                </TableCell>
                              ))}
                          </TableRow>
                        ))}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TableCell />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <TablePagination
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                    ActionsComponent={TablePaginationActions}
                  />
                </Box>
              </>
            )}
          </Box>
        </Card>
      </Box>
    </PageContainer>
  );
};

export default EightHourReportPage;
